import { createPet, listPetsByCustomer } from '@/lib/api';
import type { Pet, PetWithVaccinations } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Dog, Loader2, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Props {
  customerId: string | null;
  value: PetWithVaccinations | null;
  onChange: (p: PetWithVaccinations | null) => void;
}

interface NewPetForm {
  name: string;
  species: 'dog' | 'cat';
  breed: string;
}

const emptyForm = (): NewPetForm => ({ name: '', species: 'dog', breed: '' });

export function PetCombobox({ customerId, value, onChange }: Props) {
  const [pets, setPets] = useState<PetWithVaccinations[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<NewPetForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!customerId) {
      setPets([]);
      return;
    }
    setLoading(true);
    listPetsByCustomer(customerId)
      .then(setPets)
      .catch(() => setPets([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  const save = async () => {
    if (!customerId || !form.name.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      const created: Pet = await createPet({
        customerId,
        name: form.name.trim(),
        species: form.species,
        breed: form.breed.trim() || null,
      });
      // Wrap with empty vaccinations so PetWithVaccinations type is satisfied
      const withVaccinations: PetWithVaccinations = {
        ...created,
        vaccinations: [],
      };
      setPets((prev) => [...prev, withVaccinations]);
      onChange(withVaccinations);
      setShowCreate(false);
      setForm(emptyForm());
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to add pet');
    } finally {
      setSaving(false);
    }
  };

  if (!customerId) {
    return (
      <div className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-muted-foreground">
        Select a customer first
      </div>
    );
  }

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-border bg-surface px-3 py-2">
        <div className="flex items-center gap-2">
          <Dog className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
          <div>
            <p className="text-sm font-medium">{value.name}</p>
            {value.breed && <p className="text-xs text-muted-foreground">{value.breed}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-muted-foreground hover:text-foreground press"
        >
          Change
        </button>
      </div>
    );
  }

  if (showCreate) {
    return (
      <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
        <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          New pet
        </p>
        <input
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Pet name"
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/30"
        />
        <div className="flex gap-2">
          {(['dog', 'cat'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setForm((f) => ({ ...f, species: s }))}
              className={cn(
                'flex-1 rounded-md border px-2 py-1.5 text-xs font-medium press',
                form.species === s
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-secondary',
              )}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <input
          value={form.breed}
          onChange={(e) => setForm((f) => ({ ...f, breed: e.target.value }))}
          placeholder="Breed (optional)"
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/30"
        />
        {saveError && <div className="text-xs text-red-600">{saveError}</div>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void save()}
            disabled={!form.name.trim() || saving}
            className="flex-1 rounded-md bg-primary px-2 py-1.5 text-xs font-medium text-primary-foreground press disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add pet'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowCreate(false);
              setForm(emptyForm());
            }}
            className="rounded-md border border-border px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-2">
      {loading && (
        <div className="flex items-center gap-2 px-2 py-1 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
          Loading pets…
        </div>
      )}
      {!loading && pets.length === 0 && (
        <p className="px-2 py-1 text-sm text-muted-foreground">No pets on file</p>
      )}
      {!loading &&
        pets.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary press"
          >
            <Dog className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            <span className="font-medium">{p.name}</span>
            {p.breed && <span className="text-xs text-muted-foreground">{p.breed}</span>}
          </button>
        ))}
      <button
        type="button"
        onClick={() => setShowCreate(true)}
        className={cn(
          'flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-primary hover:bg-primary/10 press',
          pets.length > 0 && 'mt-1 border-t border-border pt-2',
        )}
      >
        <Plus className="size-3.5" strokeWidth={2} />
        Add new pet
      </button>
    </div>
  );
}
